/**
 * Dynamic PWA Manifest Loader
 *
 * Loads the appropriate manifest based on:
 * - Current route (guest vs landlord)
 * - User's language preference (hr vs en)
 * - Guest access token (if available)
 *
 * This ensures separate PWA installations for:
 * - Guest App (BookiApp - App za goste / BookiApp - Guest App)
 * - Landlord Portal (BookiApp - Portal za iznajmljivače / BookiApp - Landlord Portal)
 *
 * IMPORTANT: For Chrome installability, we need a static manifest link in HTML.
 * This function updates it as soon as possible after page load.
 */

export function loadManifest(): void {
  // Detect if we're on guest route
  const isGuestRoute = window.location.pathname.startsWith('/guest');

  // Get language from localStorage (i18next stores it there)
  let storedLang: string | null = null;
  try {
    // 'bookiapp-language' is what src/i18n writes; i18nextLng is a legacy key
    storedLang = localStorage.getItem('bookiapp-language') ?? localStorage.getItem('i18nextLng');
  } catch {
    // blocked storage (third-party iframe) — fall back to the default
  }
  // Only hr/en manifests exist (any other language → English; none → Croatian)
  const base = storedLang?.split('-')[0];
  const language = base === 'hr' || base === 'en' ? base : base ? 'en' : 'hr';

  // Determine manifest type
  const manifestType = isGuestRoute ? 'guest' : 'landlord';

  // For guest portals on /guest/portal route, use static manifest
  // (Chrome needs static manifest for installability)
  if (isGuestRoute) {
    // Use static manifest for non-guest routes or guest access page
    const manifestPath = `/${manifestType}-manifest-${language}.webmanifest`;

    const existingLink = document.querySelector('link[rel="manifest"]');
    if (existingLink) {
      existingLink.setAttribute('href', manifestPath);
    } else {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = manifestPath;
      document.head.appendChild(link);
    }

    if (import.meta.env.DEV) {
      console.log('[PWA Manifest] Loaded:', {
        route: isGuestRoute ? 'guest' : 'landlord',
        language,
        manifestPath,
      });
    }
  }
}

/**
 * Reload manifest when language changes
 * Call this after i18n language change
 */
export function reloadManifest(): void {
  loadManifest();
}
